import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { BellOutlined } from '@ant-design/icons';
import useHandleResize from '../../utils/HandleResize';
import {
  routes,
  customerRoutes,
  subRoutes,
  customerSubRoutes,
  pathSearches
} from '../../utils/constants/routes';
import navMenuLogos from '../../utils/constants/navMenuLogos';
import useAuthContext from '../../contexts/AuthContext';
import useAssurmaxContext from '../../contexts/AssurmaxContext';

const NavMenu = ({ path, setCollapsed }) => {
  const { t } = useTranslation();
  const { width } = useHandleResize();
  const location = useLocation();
  const { checkShouldDisplayMenuItem, checkRightMenuAccess, user } =
    useAuthContext();
  const assurmaxContext = useAssurmaxContext();
  const [color, setColor] = useState(false);
  const isCustomer = user?.role === 'guests:GUEST';

  const handleMenuClick = ({ key }) => {
    if (key === '/message') {
      window.location.href = 'mailto:courtiers@assurmaxpro.com';
      return;
    }
    if (width < 992) {
      setCollapsed(true);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setColor((prev) => !prev);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const menuItems = useMemo(() => {
    const routesToUse = isCustomer ? customerRoutes : routes;
    const subRoutesToUse = isCustomer ? customerSubRoutes : subRoutes;

    return Object.entries(routesToUse)
      .filter(([resourceName]) =>
        checkShouldDisplayMenuItem(resourceName.toLowerCase())
      )
      .map(([menuItem, p]) => {
        if (['QUOTES', 'CONTRACTS'].includes(menuItem)) {
          if (!checkRightMenuAccess(menuItem.toLowerCase())) return null;

          return Object.entries(subRoutes[menuItem])
            .filter(([resourceName]) =>
              checkShouldDisplayMenuItem(
                resourceName.toLowerCase(),
                menuItem.toLowerCase()
              )
            )
            .map(([subMenuItem, subPath]) => ({
              key: subPath,
              disabled: subPath === '',
              label: (
                <NavLink
                  to={`${p}${subPath}${pathSearches[subMenuItem] || ''}`}
                >
                  {navMenuLogos[subMenuItem]}
                  <span>
                    {t(`basiclayout.submenu.${subMenuItem.toLowerCase()}`)}
                    {assurmaxContext[subMenuItem] ? (
                      <>
                        {` (${assurmaxContext[subMenuItem]})`}
                        <BellOutlined
                          style={{ marginLeft: 3, color: color && 'red' }}
                        />
                      </>
                    ) : (
                      ''
                    )}
                  </span>
                </NavLink>
              )
            }));
        }

        if (subRoutesToUse[menuItem]) {
          return {
            key: p,
            icon: navMenuLogos[menuItem],
            label: t(`basiclayout.menu.${menuItem.toLowerCase()}`),
            children: Object.entries(subRoutes[menuItem])
              .filter(([resourceName]) =>
                checkShouldDisplayMenuItem(
                  resourceName.toLowerCase(),
                  menuItem.toLowerCase()
                )
              )
              .map(([subMenuItem, subPath]) => ({
                key: subPath,
                disabled: subPath === '',
                label: (
                  <NavLink
                    to={`${p}${subPath}${pathSearches[subMenuItem] || ''}`}
                  >
                    <span>
                      {t(`basiclayout.submenu.${subMenuItem.toLowerCase()}`)}
                      {assurmaxContext[subMenuItem] ? (
                        <span className="menu-item-with-bell">
                          {`(${assurmaxContext[subMenuItem]})`}
                          <BellOutlined style={{ color: color && 'red' }} />
                        </span>
                      ) : (
                        ''
                      )}
                    </span>
                  </NavLink>
                )
              }))
          };
        }

        if (!checkRightMenuAccess(menuItem.toLowerCase())) return null;

        return {
          key: p || menuItem,
          disabled: p === '',
          icon: navMenuLogos[menuItem],
          label:
            menuItem.toLowerCase() === 'messages' ? (
              <a href="mailto:courtiers@assurmaxpro.com">
                <span>
                  {t(`basiclayout.menu.${menuItem.toLowerCase()}`)}
                  {assurmaxContext[menuItem]
                    ? ` (${assurmaxContext[menuItem]})`
                    : ''}
                </span>
              </a>
            ) : (
              <NavLink to={`${p}${pathSearches[menuItem] || ''}`}>
                <span>
                  {t(`basiclayout.menu.${menuItem.toLowerCase()}`)}
                  {assurmaxContext[menuItem]
                    ? ` (${assurmaxContext[menuItem]})`
                    : ''}
                </span>
              </NavLink>
            )
        };
      })
      .flat()
      .filter(Boolean);
  }, [
    isCustomer,
    checkShouldDisplayMenuItem,
    checkRightMenuAccess,
    assurmaxContext,
    color,
    t
  ]);

  return (
    <>
      <Menu
        style={{
          padding: '8px 0',
          paddingBottom: 32,
          background: 'var(--clientColor)'
        }}
        selectedKeys={[path]}
        defaultOpenKeys={[
          ...location.pathname.split('/').map((p) => `/${p}`),
          '/contracts',
          '/quotes'
        ]}
        mode="inline"
        onClick={handleMenuClick}
        items={menuItems}
      />
    </>
  );
};

NavMenu.propTypes = {
  path: PropTypes.string.isRequired,
  setCollapsed: PropTypes.func.isRequired
};

export default NavMenu;
